import React, { useEffect, useState } from "react";
import { driverApi } from "../../services/driverApi";

const ONLINE_FORM_URL = "https://wc1.cdle.state.co.us/CDLE.WC.CoverageRejectionEntryForm/page1";

const ONLINE_STEPS = [
  { n: 1, text: "Click \"Fill Out Online Form\" below — the official Colorado CDLE form opens in a new tab." },
  { n: 2, text: "Have ready: your full legal name, LLC or business name, EIN/FEIN, ownership percentage, and contact info." },
  { n: 3, text: "Complete all pages and submit the form directly to CDLE. No notary is required for the online submission." },
  { n: 4, text: "Save or screenshot your confirmation page, then upload it below so the office has a record on file." },
];

const PAPER_STEPS = [
  { n: 1, text: "Download the blank WC43 PDF using the link below." },
  { n: 2, text: "Fill in all fields by hand — your name, LLC/business name, EIN, ownership percentage, and signature." },
  { n: 3, text: "Take the completed form to a notary public. The notary must witness your signature and stamp/seal the document." },
  { n: 4, text: "Scan or photograph the notarized form and upload it below." },
];

export default function Wc43Form() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [showPaper, setShowPaper] = useState(false);
  const [markingOnline, setMarkingOnline] = useState(false);
  const [confirmOnline, setConfirmOnline] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadUploads() {
    try {
      const { docs } = await driverApi.listUploads();
      setUploads((docs || []).filter((d) => d.documentCategory === "wc43"));
    } catch {}
  }

  useEffect(() => { loadUploads(); }, []);

  async function handleUpload(e) {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("documentCategory", "wc43");
      fd.append("label", "WC43 — Rejection of Coverage Confirmation");
      await driverApi.uploadDoc(fd);
      setFile(null);
      setSuccess("Uploaded successfully.");
      setTimeout(() => setSuccess(""), 4000);
      loadUploads();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleView(docId) {
    try {
      const { link } = await driverApi.getDocLink(docId);
      window.open(link, "_blank");
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleMarkOnline() {
    setMarkingOnline(true);
    setError("");
    try {
      await driverApi.markWc43Online();
      setConfirmOnline(false);
      setSuccess("WC43 marked as completed. The office has been notified.");
      setTimeout(() => setSuccess(""), 5000);
      loadUploads();
    } catch (err) {
      setError(err.message);
    } finally {
      setMarkingOnline(false);
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">WC43 — Rejection of Workers' Compensation Coverage</h1>
        <p className="text-sm text-gray-500 mt-1">Required by the Colorado Division of Workers' Compensation for independent contractors.</p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 mb-4 text-sm">{success}</div>}

      {/* Already uploaded */}
      {uploads.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-5">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
            <p className="text-sm font-semibold text-green-800">WC43 on file</p>
          </div>
          {uploads.map((u) => (
            <div key={u._id} className="flex items-center justify-between mt-1">
              <p className="text-xs text-green-700">{u.label} — {new Date(u.createdAt).toLocaleDateString()}</p>
              {u.dropboxPath === "__online_cdle_submission__"
                ? <span className="text-xs text-green-600 font-medium">Online submission</span>
                : <button onClick={() => handleView(u._id)} className="text-xs text-green-700 underline font-medium">View</button>
              }
            </div>
          ))}
          <p className="text-xs text-green-600 mt-2">You can upload a replacement below if needed.</p>
        </div>
      )}

      {/* Online form — primary CTA */}
      <div className="bg-blue-600 rounded-xl p-5 mb-4 text-white">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-base">Fill Out Online — Fastest Option</p>
            <p className="text-sm text-blue-100 mt-0.5 mb-3">Submit directly to CDLE through the official state website. No printing or notary required.</p>
            <a
              href={ONLINE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-blue-700 font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-blue-50 transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
              Open Official CDLE Form
            </a>
          </div>
        </div>
      </div>

      {/* Online steps */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <p className="text-sm font-semibold text-gray-800 mb-4">How to complete online</p>
        <ol className="space-y-3">
          {ONLINE_STEPS.map(({ n, text }) => (
            <li key={n} className="flex gap-3 text-sm">
              <span className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{n}</span>
              <span className="text-gray-700">{text}</span>
            </li>
          ))}
        </ol>
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2">
          <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <p className="text-xs text-blue-800">Have your <strong>LLC or business name</strong>, <strong>EIN/FEIN</strong>, and <strong>ownership percentage</strong> ready before you start the form.</p>
        </div>
      </div>

      {/* Paper fallback toggle */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setShowPaper((v) => !v)}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          {showPaper ? "Hide paper form option" : "Prefer to download and fill by hand instead?"}
        </button>
      </div>

      {showPaper && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">WC43 — Blank Paper Form</p>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">Download, fill by hand, have it notarized, then upload the completed copy below.</p>
              <a
                href="/documents/wc43_rejection_of_coverage.pdf"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-1.5 bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                Download Blank WC43
              </a>
            </div>
          </div>

          <ol className="space-y-3 mb-4">
            {PAPER_STEPS.map(({ n, text }) => (
              <li key={n} className="flex gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{n}</span>
                <span className="text-gray-700">{text}</span>
              </li>
            ))}
          </ol>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
            <svg className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
            <p className="text-xs text-yellow-800"><strong>Notarization required</strong> for the paper form. Many UPS stores, banks, and libraries offer free or low-cost notary services.</p>
          </div>
        </div>
      )}

      {/* Mark as completed online */}
      {!uploads.some((u) => u.dropboxPath === "__online_cdle_submission__") && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <p className="text-sm font-semibold text-gray-800 mb-1">Already submitted online?</p>
          <p className="text-xs text-gray-500 mb-3">If you completed the CDLE online form, tap the button below to mark this step as done. No file upload needed.</p>

          {!confirmOnline ? (
            <button type="button" onClick={() => setConfirmOnline(true)}
              className="w-full border border-blue-300 text-blue-700 font-semibold py-2.5 rounded-lg text-sm hover:bg-blue-50 transition">
              I Submitted the Form Online — Mark as Complete
            </button>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <p className="text-sm text-blue-900 font-medium">Confirm online submission</p>
              <p className="text-xs text-blue-700">By confirming, you certify that you completed and submitted the WC43 Rejection of Coverage form directly to the Colorado Division of Workers' Compensation via the official CDLE online portal.</p>
              <div className="flex gap-2">
                <button type="button" onClick={handleMarkOnline} disabled={markingOnline}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-sm transition">
                  {markingOnline ? "Saving…" : "Yes, I Submitted It Online"}
                </button>
                <button type="button" onClick={() => setConfirmOnline(false)}
                  className="flex-1 border border-gray-300 text-gray-600 font-semibold py-2 rounded-lg text-sm hover:bg-gray-50 transition">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <p className="text-sm font-semibold text-gray-800 mb-1">Upload Your Confirmation</p>
        <p className="text-xs text-gray-500 mb-4">Upload a screenshot or PDF of your CDLE submission confirmation, or the notarized paper form. Accepted: PDF, JPG, PNG. Max 20 MB.</p>

        <form onSubmit={handleUpload} className="space-y-3">
          <label className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl px-4 py-8 cursor-pointer transition ${file ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50"}`}>
            <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            {file
              ? <span className="text-sm text-blue-700 font-medium">{file.name}</span>
              : <span className="text-sm text-gray-500">Click to select or drag your file here</span>
            }
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
          </label>

          <button
            type="submit"
            disabled={!file || uploading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl text-sm transition"
          >
            {uploading ? "Uploading…" : "Upload Confirmation"}
          </button>
        </form>
      </div>
    </div>
  );
}
